import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { Coin } from "../src/components/brand";
import { Field, Segmented } from "../src/components/controls";
import { useAuth } from "../src/auth";
import { useAppObserva } from "../src/observa/use-app-observa";
import { trpc } from "../src/trpc";
import { colors, fonts, radius, space, webNoOutline } from "../src/theme";

// Sign-up / sign-in / magic-link, one screen, mode-switched (the three flows
// share an email field and most chrome). Hits the mounted /auth/* endpoints via
// useAuth(); the session cookie is set on success and flows to tRPC.
//
// Params:
//   ?claim=<assessmentId>  a logged-out result to attach to the new session
//   ?next=<path>           where to go afterwards (default /my-results)
// The common entry is from a finished result: "sign in to save this" routes
// here with claim+next, so the just-computed run lands under the user.

type Mode = "signin" | "signup" | "magic";

const MODE_OPTIONS = [
  { value: "signin", label: "Sign in" },
  { value: "signup", label: "Create account" },
  { value: "magic", label: "Magic link" },
] as const;

export default function Auth(): JSX.Element {
  const router = useRouter();
  const params = useLocalSearchParams<{ claim?: string; next?: string }>();
  const claimId = firstParam(params.claim);
  const next = firstParam(params.next) ?? "/my-results";

  const { signIn, signUp, sendMagicLink } = useAuth();
  const observa = useAppObserva();

  const [mode, setMode] = useState<Mode>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const [verifyPending, setVerifyPending] = useState(false);

  const onSubmit = (): void => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    const run = async (): Promise<void> => {
      if (mode === "magic") {
        await sendMagicLink({ email: email.trim() });
        setMagicSent(true);
        return;
      }
      if (mode === "signup") {
        const authed = await signUp({
          email: email.trim(),
          password,
          name: name.trim(),
        });
        // Verification required → no session yet. Stop here and tell the user
        // to confirm their email; the claim/navigate below assumes a session.
        if (!authed) {
          setVerifyPending(true);
          return;
        }
        // Session established (no email-verification gate). The retention
        // anchor — emitted once the user is real (getUserId now resolves it).
        void observa?.event({ eventType: "account.signup", payload: {} });
      } else {
        await signIn({ email: email.trim(), password });
        void observa?.event({
          eventType: "account.login",
          payload: { method: "password" },
        });
      }
      // Now authenticated: attach the logged-out result if we arrived with one,
      // then move on. A failed claim shouldn't block sign-in (the result is
      // still reachable by its id), so swallow it after logging.
      if (claimId !== undefined) {
        await trpc.calc.claimAssessment
          .mutate({ id: claimId })
          .catch(() => undefined);
      }
      router.replace(next);
    };

    run()
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Something went wrong.");
      })
      .finally(() => setSubmitting(false));
  };

  const submitLabel =
    mode === "magic"
      ? "Email me a link"
      : mode === "signup"
        ? "Create account"
        : "Sign in";

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.container}>
      <StatusBar style="dark" />

      <Pressable onPress={() => router.replace("/")} accessibilityRole="button">
        <Text style={styles.backLink}>← Back to calculator</Text>
      </Pressable>

      <View style={styles.header}>
        <Coin size={48} />
        <Text style={styles.kicker}>Money Back Australia</Text>
        <Text style={styles.title}>Save your results</Text>
        <Text style={styles.subtitle}>
          Create a free account to keep your calculations in one place. We store
          only your email — never your TFN, income, or bank details.
        </Text>
      </View>

      <Segmented<Mode>
        options={MODE_OPTIONS}
        value={mode}
        onChange={(m) => {
          setMode(m);
          setError(null);
          setMagicSent(false);
          setVerifyPending(false);
        }}
      />

      {mode === "magic" && magicSent ? (
        <View style={styles.notice}>
          <Text style={styles.noticeTitle}>Check your inbox</Text>
          <Text style={styles.noticeText}>
            We&apos;ve sent a sign-in link to {email.trim()}. Open it to finish
            signing in.
          </Text>
          <Text style={styles.devHint}>
            Running locally? The link is printed in the API server logs until
            email delivery is switched on.
          </Text>
        </View>
      ) : verifyPending ? (
        <View style={styles.notice}>
          <Text style={styles.noticeTitle}>Confirm your email</Text>
          <Text style={styles.noticeText}>
            Your account&apos;s created. We&apos;ve sent a confirmation link to{" "}
            {email.trim()} — open it, then sign in to save your results.
          </Text>
          <Text style={styles.devHint}>
            Running locally? The link is printed in the API server logs until
            email delivery is switched on.
          </Text>
        </View>
      ) : (
        <>
          {mode === "signup" ? (
            <Field label="Your name">
              <TextInput
                value={name}
                onChangeText={setName}
                autoCapitalize="words"
                placeholder="Alex Citizen"
                placeholderTextColor={colors.textFaint}
                style={styles.input}
                testID="auth-name"
                accessibilityLabel="Your name"
              />
            </Field>
          ) : null}

          <Field label="Email">
            <TextInput
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              textContentType="emailAddress"
              placeholder="you@example.com"
              placeholderTextColor={colors.textFaint}
              style={styles.input}
              testID="auth-email"
              accessibilityLabel="Email address"
            />
          </Field>

          {mode !== "magic" ? (
            <Field
              label="Password"
              help={mode === "signup" ? "At least 8 characters." : undefined}
            >
              <TextInput
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                textContentType="password"
                placeholder="••••••••"
                placeholderTextColor={colors.textFaint}
                style={styles.input}
                testID="auth-password"
                accessibilityLabel="Password"
              />
            </Field>
          ) : null}

          <Pressable
            onPress={onSubmit}
            accessibilityRole="button"
            accessibilityState={{ disabled: submitting }}
            disabled={submitting}
            testID="auth-submit"
            style={[styles.submit, submitting && styles.submitDisabled]}
          >
            <Text style={styles.submitText}>
              {submitting ? "Please wait…" : submitLabel}
            </Text>
          </Pressable>

          {error !== null ? <Text style={styles.error}>{error}</Text> : null}
        </>
      )}
    </ScrollView>
  );
}

/** expo-router can hand a param back as string | string[]; take the first. */
function firstParam(value: string | string[] | undefined): string | undefined {
  const v = Array.isArray(value) ? value[0] : value;
  return v === undefined || v === "" ? undefined : v;
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  container: {
    gap: space.xl,
    padding: space.xl,
    paddingBottom: space.xxl,
    maxWidth: 480,
    width: "100%",
    alignSelf: "center",
  },
  backLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  kicker: {
    fontFamily: fonts.mono,
    fontSize: 12,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: colors.honeyDeep,
    marginTop: space.xs,
  },
  title: { fontFamily: fonts.display, fontSize: 28, letterSpacing: -0.8, color: colors.ink },
  subtitle: { fontFamily: fonts.body, fontSize: 15, lineHeight: 22, color: colors.inkSoft },
  input: {
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingVertical: 14,
    paddingHorizontal: space.lg,
    fontFamily: fonts.bodyMedium,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.card,
    ...webNoOutline,
  },
  submit: {
    backgroundColor: colors.honey,
    borderRadius: radius.md,
    paddingVertical: space.lg,
    alignItems: "center",
    marginTop: space.sm,
  },
  submitDisabled: { opacity: 0.4 },
  submitText: { fontFamily: fonts.bodyBold, color: colors.ink, fontSize: 16 },
  error: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.danger, textAlign: "center" },
  notice: {
    gap: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    backgroundColor: colors.sand,
    padding: space.lg,
  },
  noticeTitle: { fontFamily: fonts.display, fontSize: 16, color: colors.ink },
  noticeText: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  devHint: { fontFamily: fonts.body, fontSize: 12, lineHeight: 18, color: colors.muted },
});
